<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import actions from "~/actions";
import { backend } from "~/api";
import PageNotFound from "~/components/dumb/PageNotFound.vue";
import TaskListAndDetail from "~/components/dumb/TaskListAndDetail.vue";
import { notify } from "~/components/notifications";
import TaskList from "~/components/visualization/TaskList.vue";
import { getQueryParam } from "~/router/common";
import { EventKind, NotificationType, ViewKind } from "~/shared/enums";
import { useAppStore, useDataStore, usePageStore, useUserStore } from "~/stores";
import { getDartboardLink, getViewLink } from "~/utils/common";
import { when } from "~/utils/wait";

const props = defineProps<{
  dartboardDuid?: string;
  viewDuid?: string;
  viewKind?: ViewKind.SEARCH | ViewKind.MY_TASKS | ViewKind.TRASH;
  taskDuidAndSlug?: string;
  commentDuid?: string;
  slug?: string;
  slugSep?: string;
}>();

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const isNotFound = ref(false);

const setSelectedPage = async () => {
  if (props.dartboardDuid) {
    const dartboard = dataStore.getDartboardByDuid(props.dartboardDuid);
    if (!dartboard) {
      isNotFound.value = true;
      pageStore.pageLoaded = true;
      return;
    }
    isNotFound.value = false;
    appStore.setCurrentPage(dartboard);

    if (!router.currentRoute.value.params.taskDuidAndSlug) {
      await when(computed(() => !!pageStore.pageLoaded));
      router.replace({
        params: { slugSep: "-", slug: getDartboardLink(dartboard, dataStore.getSpaceByDuid).params.slug },
        query: { ...router.currentRoute.value.query },
      });
    }
    return;
  }

  if (props.viewDuid) {
    const view = dataStore.getViewByDuid(props.viewDuid);
    if (!view) {
      isNotFound.value = true;
      pageStore.pageLoaded = true;
      return;
    }
    isNotFound.value = false;
    appStore.setCurrentPage(view);

    if (!router.currentRoute.value.params.taskDuidAndSlug) {
      await when(computed(() => !!pageStore.pageLoaded));
      router.replace({
        params: { slugSep: "-", slug: getViewLink(view).params?.slug },
        query: { ...router.currentRoute.value.query },
      });
    }
    return;
  }

  if (props.viewKind) {
    const view = dataStore.getViewByKind(props.viewKind);
    appStore.setCurrentPage(view);
    if (props.viewKind === ViewKind.SEARCH) {
      backend.event.create(EventKind.USAGE_OPEN_SEARCH);
    }
    return;
  }

  throw new Error(`Invalid props for TaskListRootView: ${props}`);
};

const setDetailsOnFirstLoad = async () => {
  appStore.setTaskDetailOpen(!!props.taskDuidAndSlug);
  appStore.setTaskDetailLoading(!!props.taskDuidAndSlug);
  const requestedTask = props.taskDuidAndSlug
    ? ((await dataStore.getTaskByDuid(props.taskDuidAndSlug.substring(0, 12))) ?? null)
    : null;

  if (!requestedTask) {
    appStore.setTaskDetailOpen(false);
    appStore.setTaskDetailLoading(false);
    if (props.taskDuidAndSlug) {
      isNotFound.value = true;
      pageStore.pageLoaded = true;
    }
    return;
  }

  await actions.visualization.navigateToTask(requestedTask.duid, { maintainQuery: true });

  if (getQueryParam("unsubscribe") === "1") {
    const userDuid = userStore.duid;
    if (requestedTask.subscriberDuids.includes(userDuid)) {
      dataStore.removeSubscribers([requestedTask.duid], [userDuid], { noUndo: true });
      notify({
        message: `Unsubscribed from ${requestedTask.title}`,
        type: NotificationType.SUCCESS,
      });
    }
    const query = { ...router.currentRoute.value.query };
    delete query.unsubscribe;
    await router.replace({ query });
  }
};

setSelectedPage();

onMounted(async () => {
  await nextTick();
  setDetailsOnFirstLoad();
});

watch(
  [() => props.dartboardDuid, () => props.viewDuid, () => props.viewKind, () => props.taskDuidAndSlug],
  setSelectedPage
);
</script>

<template>
  <TaskListAndDetail v-if="!isNotFound">
    <TaskList />
  </TaskListAndDetail>
  <PageNotFound v-else />
</template>
